/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { trackClickEvent } from "@eisinc/App/CoveoConnector/Services/CoveoService";
import { ClickEventRequest } from "@eisinc/App/CoveoConnector/Types/schema";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { isProductInActive } from "@eisinc/App/Utilities/helpers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* eslint-disable */
const HTMLEllipsis = require("@eisinc/Libraries/react-lines-ellipsis/html.js");
/* eslint-enable */

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
    showElipses?: boolean;
    showElipsesOn?: string;

    widgetId?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;


// xng-change
const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const widgetId = ownProps.widgetId;
    const widget = state.WidgetsCoveo?.widgets.find(widget => widget.widgetId === widgetId);
    const analyticsObject = widget?.analyticsObject || {} as any;

    return {
        originLevel1: state.xCoveo.searchHub,
        searchHub: state.xCoveo.searchHub,
        trackCoveo: state.xCoveo.searchHub,
        analyticsObject,
    }
}


export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
    productDetailDescription: {
        size: 14,
    },
};
const ProductDescription = ({
    product,
    onClick,
    extendedStyles,
    showElipses,
    showElipsesOn,

    // xng-change
    originLevel1,
    searchHub,
    trackCoveo,
    analyticsObject,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productDescriptionStyles, extendedStyles));

    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");
    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    const customOnClick = (e: React.MouseEvent) => {
        const targetProduct = "product" in product ? product.product : product as any;
        const productAnalyticsObject = analyticsObject?.items.find((item: any) => item.id === targetProduct.id);

        if (trackCoveo && productAnalyticsObject) {
            const documentOpenEventParameter: ClickEventRequest = {
                actionCause: "documentOpen",
                searchQueryUid: analyticsObject?.searchQueryUid || translate("my-uid"),
                originLevel1: searchHub,
                searchHub,
                documentUri: targetProduct.uri,
                collectionName: targetProduct.brand?.name as string,
                sourceName: analyticsObject?.sourceName || translate("sourceName"),
                documentPosition: analyticsObject?.items.findIndex((item: any) => item.id === targetProduct.id) || 1,
                documentTitle: productAnalyticsObject?.title || targetProduct.productTitle,
                documentUrl: productAnalyticsObject?.url || targetProduct.uri,
                documentAuthor: productAnalyticsObject.documentAuthor || "",
                queryPipeline: analyticsObject?.queryPipeline as string,
                rankingModifier: productAnalyticsObject.rankingModifier || "",
                permanentId: productAnalyticsObject.permanentId || "",
                uriHash: productAnalyticsObject.uriHash || "",
                customData: {}, // we have to send it even if it's an empty object because BE will throw error if it is null
            };
            trackClickEvent(documentOpenEventParameter);
        }

        onClick?.(e);
    };

    if (productDetailPath && !isProductInActive("product" in product ? product.product : product)) {
        return (
            <Link
                {...styles.productDetailLink}
                href={productDetailPath}
                alt={`See product details for ${description}`}
                onClick={customOnClick}
                data-test-selector="productDescriptionLink"
            >
                {showElipses ? (
                    <HTMLEllipsis
                        unsafeHTML={description}
                        maxLine={showElipsesOn}
                        ellipsisHTML="..."
                        basedOn="letters"
                    />
                ) : (
                    description
                )}
            </Link>
        );
    }

    return (
        <Typography {...styles.productDetailDescription}>
            {showElipses ? (
                <HTMLEllipsis unsafeHTML={description} maxLine={showElipsesOn} ellipsisHTML="..." basedOn="letters" />
            ) : (
                description
            )}
        </Typography>
    );
};

export default connect(mapStateToProps)(ProductDescription);
